import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import MouseBackground from './lib/components/MouseBackground';
import Navigation from './lib/containers/nav';
import Banner from './lib/containers/banner';
import About from './lib/containers/about';
import Organizations from './lib/containers/organizations';
import Works from './lib/containers/work';
import Skills from './lib/containers/skills';
import Footer from './lib/containers/footer';
import { motion, AnimatePresence } from 'framer-motion';
import CircularProgress from '@mui/material/CircularProgress';

// Loading screen component
function LoadingScreen() {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0A0A0F',
        zIndex: 9999,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box
          sx={{
            fontFamily: 'Fira Code, monospace',
            fontSize: { xs: '1.5rem', md: '2rem' },
            color: '#44D482',
            mb: 4,
          }}
        >
          $ loading<span className="terminal-cursor">_</span>
        </Box>
        <CircularProgress 
          size={40}
          sx={{ 
            color: '#44D482',
            display: 'block',
            margin: '0 auto',
          }} 
        />
      </motion.div>
    </Box>
  );
}

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check window size
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 900);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <AnimatePresence>
        {isLoading && (
          <motion.div
            key="loading"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <LoadingScreen />
          </motion.div>
        )}
      </AnimatePresence>

      {!isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* Background with mouse reactivity */}
          <MouseBackground />

          {/* Navigation */}
          <Navigation />

          {/* Main content */}
          <Box
            component="main"
            sx={{
              position: 'relative',
              zIndex: 1,
              minHeight: '100vh',
            }}
          >
            <Banner isMobile={isMobile} />
            <About isMobile={isMobile} />
            <Organizations isMobile={isMobile} />
            <Works isMobile={isMobile} />
            <Skills isMobile={isMobile} />
            <Footer />
          </Box>
        </motion.div>
      )}
    </>
  );
}

export default App;
